import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';

import { addTrackingAsyncThunk, getAwaitingAsyncThunk } from '../../store/thunks/packages.thunk';

import Input from '../input/Input';
import { Airplane2Icon, AirplaneIcon, PackagesMainIcon, Truck2Icon, TruckIcon } from '../../assets/icons';
import Button from '../Button';
import GlobalContext from '../GlobalContext';
import Resolution from '../hooks/resolution';
import CountryChooser from './CountryChooser';
import { useQueryClient } from 'react-query';
import Modal from '../modal/Modal';

const AddTrackingNew = () => {
  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setToken, setAlert } = useContext(GlobalContext);
  const navigate = useNavigate();

  // Local State
  const [trackingCode, setTrackingCode] = useState('');
  const [message, setMessage] = useState('');

  const [country, setCountry] = useState(null);
  const [showAddTracking, setShowAddTracking] = useState(false);

  const [showTransportMethodModal, setShowTransportMethodModal] = useState(false);

  // Redux State
  const addTrackingLoading = useSelector((state) => state.packages.customLoadings.addTracking);

  // Variables
  const resolution = Resolution();
  const queryClient = useQueryClient();

  // Handlers
  const addNewTracking = async (e) => {
    e.preventDefault();
    if (trackingCode.trim().length === 0) {
      setMessage(t('_INVALID_TRACKING'));
    } else if (country === null) {
      setMessage(t('_INVALID_ADD_TRACKING_COUNTRY'));
    } else {
      if (country === 'CHINA') {
        setShowTransportMethodModal(true);
      } else {
        dispatch(addTrackingAsyncThunk({ tracking: trackingCode, country: country, transportMethod: null })).then(({ payload }) => {
          if (payload?.code === 102) {
            navigate('/');
            setToken(null);
          } else if (payload?.status === 'OK') {
            setAlert(['success', t(payload.message)]);
            setTrackingCode('');
            setMessage('');
            setShowAddTracking(false);
            setCountry(null);
            queryClient.invalidateQueries('userInfo');
            dispatch(getAwaitingAsyncThunk()).then(({ payload }) => {
              if (payload?.code === 102) {
                navigate('/');
                setToken(null);
              }
            });
          } else {
            setMessage(t(payload.message));
          }
        });
      }
    }
  };

  const addPlaneTracking = () => {
    setShowTransportMethodModal(false);
    dispatch(addTrackingAsyncThunk({ tracking: trackingCode, country: country, transportMethod: 'PLANE' })).then(({ payload }) => {
      if (payload?.code === 102) {
        navigate('/');
        setToken(null);
      } else if (payload?.status === 'OK') {
        setAlert(['success', t(payload.message)]);
        setTrackingCode('');
        setMessage('');
        setShowAddTracking(false);
        setCountry(null);
        queryClient.invalidateQueries('userInfo');
        dispatch(getAwaitingAsyncThunk()).then(({ payload }) => {
          if (payload?.code === 102) {
            navigate('/');
            setToken(null);
          }
        });
      } else {
        setMessage(t(payload.message));
      }
    });
  };

  const addTruckTracking = () => {
    setShowTransportMethodModal(false);
    dispatch(addTrackingAsyncThunk({ tracking: trackingCode, country: country, transportMethod: 'TRUCK' })).then(({ payload }) => {
      if (payload?.code === 102) {
        navigate('/');
        setToken(null);
      } else if (payload?.status === 'OK') {
        setAlert(['success', t(payload.message)]);
        setTrackingCode('');
        setMessage('');
        setShowAddTracking(false);
        setCountry(null);
        queryClient.invalidateQueries('userInfo');
        dispatch(getAwaitingAsyncThunk()).then(({ payload }) => {
          if (payload?.code === 102) {
            navigate('/');
            setToken(null);
          }
        });
      } else {
        setMessage(t(payload.message));
      }
    });
  };

  return (
    <div className='bg-white mb-4 p-4 pb-1 border-rad-12'>
      <Row>
        <Col lg={7} md={6} xs={12}>
          <div>
            <h2 className='color-orange font-size-16'>{t('_ADD_PACKAGE')}</h2>
            <div className='h-20px mb-3'>
              <p className='color-red font-size-14'>{message}</p>
            </div>
            <form onSubmit={addNewTracking}>
              <Input
                onChange={(e) => setTrackingCode(e.target.value)}
                value={trackingCode}
                onFocus={() => {
                  setShowAddTracking(true);
                }}
                label={t('_TRACKING_CODE')}
              />
              <div className={`add-tracking-btn-new ${showAddTracking}`}>
                <CountryChooser country={country} setCountry={setCountry} />
                <div className='add-tracking-buttons'>
                  <Button type='submit' text={t('_ADD')} btnStyle='primary-btn-w-h color-white bg-orange' isLoading={addTrackingLoading} />
                  <Button
                    type='button'
                    text={t('_CANCEL')}
                    btnStyle='primary-btn-w-h '
                    handleClick={() => {
                      setShowAddTracking(false);
                      setTrackingCode('');
                      setMessage('');
                      setCountry(null);
                    }}
                  />
                </div>
              </div>
            </form>
          </div>
        </Col>
        {resolution <= 768 ? null : (
          <Col lg={5} md={6}>
            <div className='add-tracking-icon'>
              <PackagesMainIcon />
            </div>
          </Col>
        )}
      </Row>
      <Modal showModal={showTransportMethodModal} setShowModal={setShowTransportMethodModal} modal={false}>
        <div className='d-flex flex-column'>
          <p
            className='modalText'
            style={{ maxHeight: 500, overflow: 'auto', paddingRight: 15 }}
            dangerouslySetInnerHTML={{ __html: t('_ADD_TRACKING_CHINA_TRANSPORTATION_METHOD') }}
          ></p>
          <div className='d-flex align-items-center justify-content-center modal-btn-div mt-4' style={{ gap: 10 }}>
            <div
              onClick={addPlaneTracking}
              style={{
                cursor: 'pointer',
                border: '1px solid #f68424',
                padding: 10,
                paddingTop: 25,
                diplay: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyItems: 'center',
                width: '100%',
                minHeight: 140,
                borderRadius: 12
              }}
            >
              <Airplane2Icon style={{ width: 30 }} />
              <p style={{ textAlign: 'center' }}>{t('_PLANE_METHOD')}</p>
            </div>
            <div
              onClick={addTruckTracking}
              style={{
                cursor: 'pointer',
                border: '1px solid #f68424',
                padding: 10,
                paddingTop: 25,
                diplay: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyItems: 'center',
                // gap: 10,
                width: '100%',
                minHeight: 140,
                borderRadius: 12
              }}
            >
              <Truck2Icon style={{ width: 30 }} />
              <p style={{ textAlign: 'center' }}>{t('_TRUCK_METHOD')}</p>
            </div>
            {/* <Button
              handleClick={addPlaneTracking}
              type='button'
              btnStyle='button-transport-method me-4 fill-balance-btn'
              text={t('_PLANE')}
              color='black'
              isLoading={false}
              icon={<Airplane2Icon style={{ width: 20 }} />}
            /> */}
            {/* <Button
              handleClick={addTruckTracking}
              type='button'
              btnStyle='bg-orange me-4 fill-balance-btn'
              text={t('_TRUCK')}
              color='color-white'
              isLoading={false}
              icon={<Truck2Icon />}
            /> */}
            {/* <Button handleClick={addTruckTracking} type='button' btnStyle='bg-orange fill-balance-btn' text={t('_TRUCK')} color='color-white' /> */}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddTrackingNew;
